<template>
  <b-card>
    <validation-observer ref="simpleRules">
      <b-form>
        <h4>{{ $t('Maintenance') }}</h4>
        <span class="font-weight-bold">{{ $t('The fields with (*) are required') }}</span>
        <hr class="mb-2">

        <b-row>
          <b-col cols="12" md="4">
            <b-form-group :label="$t('Asset') + '*'" label-for="assetId">
              <validation-provider #default="{ errors }" name="assetId" rules="required">
                <vue-autosuggest
                    v-model="assetSearch"
                    :suggestions="assetSuggestions"
                    :input-props="{
                      id: 'assetId',
                      placeholder: isPrefillingAsset ? $t('Loading') : $t('Type to search for an asset...'),
                      class: 'form-control',
                      name: 'assetId',
                      disabled: Boolean(myId) || isPrefillingAsset
                    }"
                    :render-suggestion="renderSuggestionAsset"
                    :get-suggestion-value="getAssetSuggestionValue"
                    @selected="onAssetSelected"
                    @input="onAssetInput"
                />
                <small v-if="isAssetPrefilled && !myId" class="text-muted d-block mt-25">
                  {{ $t('You can change the selected asset if needed.') }}
                </small>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4">
            <b-form-group :label="$t('Preventive Maintenance')" label-for="preventiveMaintenanceId">
              <validation-provider #default="{ errors }" name="preventiveMaintenanceId" rules="">
                <vue-autosuggest
                    v-model="preventiveMaintenanceSearch"
                    :suggestions="preventiveMaintenanceSuggestions"
                    :input-props="{
                      id: 'preventiveMaintenanceId',
                      placeholder: !maintenanceForm.assetId ? $t('Select an asset first') : $t('Preventive maintenance (optional)'),
                      class: 'form-control',
                      disabled: !maintenanceForm.assetId
                    }"
                    v-b-tooltip.hover
                    :title="!maintenanceForm.assetId ? $t('An asset must be selected to search for a maintenance plan.') : ''"
                    :render-suggestion="renderSuggestionPreventiveMaintenance"
                    :get-suggestion-value="getPreventiveMaintenanceSuggestionValue"
                    @selected="onPreventiveMaintenanceSelected"
                    @input="onPreventiveMaintenanceInput"
                />
                <small v-if="maintenanceForm.assetId" class="text-muted d-block mt-25">
                  {{ $t('Optional. Open to pick a plan step.') }}
                </small>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4">
            <b-form-group :label="$t('Driver Name')" label-for="driverName">
              <validation-provider #default="{ errors }" name="driverName" rules="max:100">
                <b-form-input
                    id="driverName"
                    v-model="maintenanceForm.driverName"
                    :placeholder="$t('Responsible Name')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row v-if="maintenanceForm.assetId" class="mb-1">
          <b-col cols="12">
            <asset-preventive-plans-panel
              :asset-id="maintenanceForm.assetId"
              :selected-step-id="maintenanceForm.preventiveMaintenanceId"
              @select="onPreventiveStepSelected"
            />
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" md="4">
            <b-form-group :label="$t('Start Date') + '*'" label-for="start-date">
              <validation-provider #default="{ errors }" name="startDate" rules="required">
                <flat-pickr
                    id="start-date"
                    v-model="maintenanceForm.startDate"
                    :config="flatpickrStartConfig"
                    class="form-control"
                    :class="{'is-invalid': errors.length > 0}"
                    :placeholder="$t('Select the start date and time')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4">
            <b-form-group :label="$t('End Date')" label-for="end-date">
              <validation-provider #default="{ errors }" name="endDate" rules="">
                <flat-pickr
                    id="end-date"
                    v-model="maintenanceForm.endDate"
                    :config="flatpickrEndConfig"
                    class="form-control"
                    :placeholder="$t('Select the end date and time (optional)')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4">
            <b-form-group :label="$t('Additional Code')" label-for="additionalCode">
              <validation-provider #default="{ errors }" name="additionalCode" rules="max:50">
                <b-form-input
                    id="additionalCode"
                    v-model="maintenanceForm.additionalCode"
                    :placeholder="$t('Reference Code')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" md="3">
            <b-form-group :label="$t('Odometer (km)')" label-for="odometer">
              <validation-provider #default="{ errors }" name="odometer" rules="">
                <b-form-input
                    id="odometer"
                    v-model.number="maintenanceForm.odometer"
                    type="number"
                    min="0"
                    :placeholder="$t('Mileage')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="3">
            <b-form-group :label="$t('Hourmeter (h)')" label-for="hourmeter">
              <validation-provider #default="{ errors }" name="hourmeter" rules="">
                <b-form-input
                    id="hourmeter"
                    v-model.number="maintenanceForm.hourmeter"
                    type="number"
                    min="0"
                    
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="3">
            <b-form-group :label="$t('Invoice')" label-for="invoice">
              <validation-provider #default="{ errors }" name="invoice" rules="max:50">
                <b-form-input
                    id="invoice"
                    v-model="maintenanceForm.invoice"
                    :placeholder="$t('Invoice number')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="3">
            <b-form-group :label="$t('Total') + '*'" label-for="value">
              <validation-provider #default="{ errors }" name="value" rules="required">
                <money v-model="maintenanceForm.value" v-bind="money" class="form-control"
                       :class="{'is-invalid': errors.length > 0}"
                       :disabled="maintenanceForm.items.length > 0"
                       v-b-tooltip.hover
                       :title="maintenanceForm.items.length > 0 ? $t('Total calculated automatically from items') : $t('Enter the value or add items for automatic calculation')"
                       :placeholder="$t('R$ 0,00')"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" md="12">
            <b-form-group :label="$t('Notes')" label-for="note">
              <validation-provider #default="{ errors }" name="note" rules="max:500">
                <b-form-textarea
                    id="note"
                    v-model="maintenanceForm.note"
                    rows="3"
                    :placeholder="$t('Notes about maintenance')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <h4 class="mt-3">{{ $t('Maintenance Items') }}</h4>
        <hr class="mb-2">

        <div class="d-flex justify-content-between align-items-center mb-3">
          <span class="font-weight-bold">{{ $t('Add maintenance items') }}</span>
          <b-button
              variant="outline-success"
              size="sm"
              @click="addMaintenanceItem"
          >
            <feather-icon icon="PlusIcon" class="mr-50"/>
            {{ $t('Add Item') }}
          </b-button>
        </div>

        <b-alert v-if="maintenanceForm.items.length === 0" show variant="info" class="py-1 text-center py-1">
          <feather-icon icon="InfoIcon"/>
          {{ $t("No items added. Click 'Add Item' to start.") }}
        </b-alert>

        <div v-for="(item, index) in maintenanceForm.items" :key="index" class="mb-3">
          <b-card class="border-left-primary">
            <b-row align-v="center">
              <b-col md="6">
                <b-form-group :label="$t('Maintenance Type') + ' ' + (index + 1) + '*'"
                              :label-for="`itemType-${index}`">
                  <validation-provider #default="{ errors }" :name="`itemType-${index}`" rules="required">
                    <vue-autosuggest
                        v-model="item.maintenanceTypeSearch"
                        :suggestions="item.filteredOptions || []"
                        :input-props="{
                        id: `itemType-${index}`,
                        placeholder: $t('Type to search for a maintenance type...'),
                        class: 'form-control',
                        'name': `itemType-${index}`
                      }"
                        :get-suggestion-value="getSuggestionValue"
                        :render-suggestion="renderSuggestion"
                        @selected="(suggestion) => onMaintenanceTypeSelected(suggestion, index)"
                        @input="(inputData, event) => onMaintenanceTypeInput(inputData, index, event)"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col md="4">
                <b-form-group :label="$t('Value') + '*'" :label-for="`itemValue-${index}`">
                  <validation-provider #default="{ errors }" :name="`itemValue-${index}`" rules="required">
                    <money v-model.number="item.value" v-bind="money" class="form-control"
                           :class="{'is-invalid': errors.length > 0}"
                           :placeholder="$t('R$ 0,00')"/>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col  md="2" class="text-right">
                <b-button
                    variant="outline-danger"
                    size="sm"
                    @click="removeMaintenanceItem(index)"
                    v-b-tooltip.hover
                    :title="$t('Remove item')"
                >
                  <feather-icon icon="TrashIcon"/>
                </b-button>
              </b-col>
            </b-row>
          </b-card>
        </div>

        <hr class="my-4">

        <b-col v-if="!user.role.endsWith('VI')" cols="12">
          <div class="d-flex justify-content-end">
            <b-button variant="secondary" class="mr-2" @click="resetForm">{{ $t('Clear') }}</b-button>
            <b-button variant="primary" @click.prevent="validationForm" :disabled="isSubmitting || !isFormValid">
              <b-spinner v-if="isSubmitting" small class="mr-1"/>
              {{ isSubmitting ? $t('Saving...') : (isEditing ? $t('Update') : $t('Save')) }}
            </b-button>
          </div>
        </b-col>
      </b-form>
    </validation-observer>

    <b-modal
        id="modal-new-maintenance-type"
        v-model="showMaintenanceTypeModal"
        :title="$t('New Maintenance Item Type')"
        size="lg"
        hide-footer
        @hide="resetMaintenanceTypeForm"
    >
      <validation-observer ref="maintenanceTypeRules">
        <b-form @submit.prevent="submitMaintenanceType">
          <b-row>
            <b-col md="6">
              <b-form-group :label="$t('Name') + '*'" label-for="typeName">
                <validation-provider #default="{ errors }" name="typeName" rules="required|min:3|max:100">
                  <b-form-input id="typeName" v-model="maintenanceTypeForm.name"
                                :state="errors.length > 0 ? false : null" :placeholder="$t('Ex: Oil change')"/>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group :label="$t('Additional Code')" label-for="typeAdditionalCode">
                <validation-provider #default="{ errors }" name="typeAdditionalCode" rules="max:50">
                  <b-form-input id="typeAdditionalCode" v-model="maintenanceTypeForm.additionalCode"
                                :placeholder="$t('Reference Code')"/>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <b-form-group :label="$t('Description') + '*'" label-for="typeDescription">
                <validation-provider #default="{ errors }" name="typeDescription" rules="required|min:3|max:500">
                  <b-form-textarea id="typeDescription" v-model="maintenanceTypeForm.description"
                                   :state="errors.length > 0 ? false : null" rows="3"
                                   :placeholder="$t('Describe the maintenance type...')"/>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <b-form-group :label="$t('Expected Hours Lifetime')" label-for="typeHoursLifetime">
                <validation-provider #default="{ errors }" name="typeHoursLifetime" rules="">
                  <b-form-input id="typeHoursLifetime" v-model.number="maintenanceTypeForm.hoursExpectedLifetime"
                                type="number" min="0" step="0.1" :placeholder="$t('Ex: 1000')"/>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group :label="$t('Expected Km Lifetime')" label-for="typeKmLifetime">
                <validation-provider #default="{ errors }" name="typeKmLifetime" rules="">
                  <b-form-input id="typeKmLifetime" v-model.number="maintenanceTypeForm.kmExpectedLifetime"
                                type="number" min="0" step="0.1" :placeholder="$t('Ex: 10000')"/>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <div class="d-flex justify-content-end mt-3">
            <b-button variant="secondary" class="mr-2" @click="showMaintenanceTypeModal = false">{{
                $t('Cancel')
              }}
            </b-button>
            <b-button type="submit" variant="primary" :disabled="isSubmittingType">
              <b-spinner v-if="isSubmittingType" small class="mr-1"/>
              {{ isSubmittingType ? $t('Saving...') : $t('Save') }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </b-modal>
  </b-card>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {Money} from 'v-money'
import {
  BAlert, BButton, BCard, BCol, BForm, BFormGroup, BFormInput, BFormTextarea, BModal, BRow, BSpinner, VBTooltip
} from 'bootstrap-vue'
import store from '@/store'
import {onUnmounted} from '@vue/composition-api'
import managementStoreModule from "@/views/main/management/managementStoreModule"
import {VueAutosuggest} from 'vue-autosuggest'
import router from '@/router'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css';
import {Portuguese} from 'flatpickr/dist/l10n/pt'
import {ValidationObserver, ValidationProvider} from 'vee-validate'
import {email, max, min, required} from '@validations'
import AssetPreventivePlansPanel from './components/AssetPreventivePlansPanel.vue'

export default {
  name: 'MaintenanceForm',
  components: {
    BFormInput, BFormGroup, BForm, BRow, BCol, BButton, BFormTextarea,
    BModal, BSpinner, BCard, BAlert,
    ToastificationContent, VueAutosuggest,
    ValidationProvider, ValidationObserver, Money,
    AssetPreventivePlansPanel,
    flatPickr
  },
  directives: {'b-tooltip': VBTooltip},
  data() {
    return {
      money: {decimal: ',', thousands: '.', prefix: 'R$ ', precision: 2, masked: false},
      myId: router.currentRoute.params.id,
      required, email, min, max,
      isEditing: false,
      isPrefillingAsset: false,
      isAssetPrefilled: false,
      isApplyingPreventiveStep: false,
      isSubmitting: false,
      isSubmittingType: false,
      showMaintenanceTypeModal: false,
      assetSearch: '',
      assetSuggestions: [],
      preventiveMaintenanceSearch: '',
      preventiveMaintenanceSuggestions: [],
      editingItemIndex: null,
      debounce: null,
      flatpickrStartConfig: {
        locale: Portuguese,
        enableTime: true,
        dateFormat: 'Y-m-d\\TH:i:S',
        altInput: true,
        altFormat: 'd/m/Y H:i',
        time_24hr: true
      },
      flatpickrEndConfig: {
        locale: Portuguese,
        enableTime: true,
        dateFormat: 'Y-m-d\\TH:i:S',
        altInput: true,
        altFormat: 'd/m/Y H:i',
        time_24hr: true,
        minDate: null
      },
      maintenanceForm: {
        assetId: '', preventiveMaintenanceId: null, note: '', value: 0,
        odometer: null, hourmeter: null, driverName: '', invoice: '',
        startDate: new Date(), endDate: null, additionalCode: '', items: []
      },
      maintenanceTypeForm: {
        customerId: null, userId: null, name: '', description: '',
        additionalCode: '', hoursExpectedLifetime: null, kmExpectedLifetime: null
      }
    }
  },

  watch: {
    'maintenanceForm.items': {
      handler(newItems) {
        if (newItems && newItems.length > 0) {
          this.maintenanceForm.value = newItems.reduce((total, item) => {
            return total + (Number(item.value) || 0);
          }, 0);
        }
      },
      deep: true,
    },

    'maintenanceForm.startDate'(newDate) {
      this.flatpickrEndConfig.minDate = newDate;
      if (this.maintenanceForm.endDate && this.maintenanceForm.endDate < newDate) {
        this.maintenanceForm.endDate = null;
      }
    }
  },

  computed: {
    isFormValid() {
      return this.maintenanceForm.assetId &&
          this.maintenanceForm.startDate &&
          this.maintenanceForm.value >= 0 &&
          this.maintenanceForm.items.every(item => item.maintenanceTypeId && item.value !== null)
    }
  },

  mounted() {
    if (this.myId) {
      this.isEditing = true
      this.loadMaintenanceData()
    } else {
      this.resetForm()
      this.prefillAssetFromQuery()
    }
  },

  methods: {
    loadMaintenanceData() {
      store
          .dispatch(`${this.MANAGEMENT_APP_STORE_MODULE_NAME}/fetchMaintenance`, this.myId)
          .then(response => {
            const data = response.data && response.data.data ? response.data.data : response.data;
            this.assetSearch = data.assetNickname || data.assetPlate || data.assetChassisNumber || '';

            const preventiveMaintenanceName = data.preventiveMaintenanceName || data.PreventiveMaintenanceName;
            if (preventiveMaintenanceName) {
              this.preventiveMaintenanceSearch = preventiveMaintenanceName;
            }

            const formattedItems = (data.items || []).map(item => ({
              maintenanceTypeId: item.maintenanceTypeId,
              maintenanceTypeSearch: item.maintenanceTypeName, 
              value: item.value,
              id: item.id,
              filteredOptions: []
            }));

            this.maintenanceForm = {
              ...data,
              items: formattedItems, 
            };
          })
          .catch(() => {
            this.msgDialog(this.$t('Error searching maintenance plans'), 'error');
          })
    },

    validationForm() {
      this.$refs.simpleRules.validate().then(success => {
        if (success) {
          this.submitMaintenance()
        }
      })
    },

    addMaintenanceItem() {
      this.maintenanceForm.items.push({
        maintenanceTypeId: '', maintenanceTypeSearch: '',
        value: 0, filteredOptions: []
      })
    },

    removeMaintenanceItem(index) {
      this.maintenanceForm.items.splice(index, 1)
    },

    getSuggestionValue(suggestion) {
      if (suggestion.item.isAddNew) {
        return this.maintenanceForm.items[this.editingItemIndex].maintenanceTypeSearch;
      }
      return suggestion.item.name
    },

    renderSuggestion(suggestion) {
      return suggestion.item.name;
    },

    renderSuggestionAsset(suggestion) {
      return suggestion.item.nickName || suggestion.item.plate || suggestion.item.chassisNumber
    },

    renderSuggestionPreventiveMaintenance(suggestion) {
      const item = suggestion.item
      if (item.planName) return `${item.planName} — ${item.name}`
      return item.name
    },

    onMaintenanceTypeSelected(suggestion, index) {
      if (!suggestion || !suggestion.item) return;

      if (suggestion.item.isAddNew) {
        this.editingItemIndex = index;
        this.prepareNewMaintenanceTypeForm(this.maintenanceForm.items[index].maintenanceTypeSearch);
        this.showMaintenanceTypeModal = true;
      } else {
        this.maintenanceForm.items[index].maintenanceTypeId = suggestion.item.id
        this.maintenanceForm.items[index].maintenanceTypeSearch = suggestion.item.name
        this.maintenanceForm.items[index].filteredOptions = []
      }
    },

    debounceSearch(callback) {
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        callback()
      }, 500)
    },

    onMaintenanceTypeInput(text, index, event) {
      if (event && event.type !== 'input') return;

      this.maintenanceForm.items[index].maintenanceTypeSearch = text
      this.maintenanceForm.items[index].maintenanceTypeId = ''
      this.editingItemIndex = index;

      if (!text || text.length < 2) {
        this.maintenanceForm.items[index].filteredOptions = []
        return
      }

      this.debounceSearch(() => {
        this.fetchSearchMaintenanceItemType(text, (results) => {
          const exactMatch = results.some(r => r.name.toLowerCase() === text.toLowerCase());
          const addNewOption = { isAddNew: true, name: this.$t('Add new maintenance type') };

          if (!exactMatch) {
            results.push(addNewOption);
          }

          this.maintenanceForm.items[index].filteredOptions = [{data: results}]
        })
      });
    },

    getAssetSuggestionValue(suggestion) {
      return suggestion.item.nickName || suggestion.item.plate || suggestion.item.chassisNumber
    },

    onAssetSelected(suggestion) {
      if (suggestion && suggestion.item) {
        this.maintenanceForm.assetId = suggestion.item.id;
        this.assetSearch = suggestion.item.nickName || suggestion.item.plate || suggestion.item.chassisNumber;
        this.assetSuggestions = [];
        this.isAssetPrefilled = false
      }
    },

    // ALTERADO: Lógica adicionada para limpar o campo de manutenção preventiva
    onAssetInput(text) {
      if (this.isPrefillingAsset) {
        this.assetSearch = text
        return
      }

      this.assetSearch = text;
      this.maintenanceForm.assetId = '';
      this.isAssetPrefilled = false

      this.preventiveMaintenanceSearch = '';
      this.maintenanceForm.preventiveMaintenanceId = null;
      this.preventiveMaintenanceSuggestions = [];

      if (!text || text.length < 2) {
        this.assetSuggestions = [];
        return;
      }

      this.debounceSearch(() => {
        this.fetchAssets(text, (results) => {
          this.assetSuggestions = [{data: results}];
        });
      });
    },

    getPreventiveMaintenanceSuggestionValue(suggestion) {
      return suggestion.item.name;
    },

    onPreventiveMaintenanceSelected(suggestion) {
      if (suggestion && suggestion.item) {
        this.maintenanceForm.preventiveMaintenanceId = suggestion.item.preventiveMaintenanceId;
        this.preventiveMaintenanceSearch = suggestion.item.name;
        this.preventiveMaintenanceSuggestions = [];
      }
    },

    onPreventiveStepSelected(step) {
      if (!step || !step.id) return
      this.isApplyingPreventiveStep = true
      this.maintenanceForm.preventiveMaintenanceId = step.id
      this.preventiveMaintenanceSearch = step.planName ? `${step.planName} — ${step.name}` : step.name
      this.preventiveMaintenanceSuggestions = []
      this.$nextTick(() => {
        this.isApplyingPreventiveStep = false
      })
    },

    onPreventiveMaintenanceInput(text) {
      if (this.isApplyingPreventiveStep) {
        this.preventiveMaintenanceSearch = text
        return
      }

      this.preventiveMaintenanceSearch = text;
      this.maintenanceForm.preventiveMaintenanceId = null;

      if (!text || text.length < 2) {
        this.preventiveMaintenanceSuggestions = [];
        return;
      }

      this.debounceSearch(() => {
        this.fetchPreventiveMaintenance(text, (results) => {
          this.preventiveMaintenanceSuggestions = [{data: results}];
        });
      });
    },

    async submitMaintenance() {
      this.isSubmitting = true;
      try {
        const parseIntegerOrNull = val => {
          if (val === '' || val === null || val === undefined) return null;
          const parsed = parseInt(val, 10);
          return Number.isFinite(parsed) ? parsed : null;
        };

        const parseNumberOrNull = val => {
          if (val === '' || val === null || val === undefined) return null;
          const parsed = Number(val);
          return Number.isFinite(parsed) ? parsed : null;
        };

        let maintenanceData = {
          assetId: this.maintenanceForm.assetId,
          preventiveMaintenanceId: this.maintenanceForm.preventiveMaintenanceId || null,
          note: this.maintenanceForm.note || null,
          value: Number(this.maintenanceForm.value) || 0,
          odometer: parseIntegerOrNull(this.maintenanceForm.odometer),
          hourmeter: parseNumberOrNull(this.maintenanceForm.hourmeter),
          driverName: this.maintenanceForm.driverName || null,
          invoice: this.maintenanceForm.invoice || null,
          startDate: this.maintenanceForm.startDate,
          endDate: this.maintenanceForm.endDate ? this.maintenanceForm.endDate : null,
          additionalCode: this.maintenanceForm.additionalCode || null,
          items: this.maintenanceForm.items.map(item => ({
            id: item.id,
            maintenanceTypeId: item.maintenanceTypeId,
            value: Number(item.value) || 0,
          })),
        };

        if (this.isEditing) {
          maintenanceData.id = this.myId;
          await store.dispatch(`${this.MANAGEMENT_APP_STORE_MODULE_NAME}/updateMaintenance`, {
            maintenanceData
          });
          this.msgDialog(this.$t('Successful update'), 'success');
          this.loadMaintenanceData()
        } else {
          await store.dispatch(`${this.MANAGEMENT_APP_STORE_MODULE_NAME}/addMaintenance`, {maintenanceData});
          this.msgDialog(this.$t('Successful registration'), 'success');
          router.push({
            name: 'list-maintenance'
          })
        }

      } catch (error) {
        this.msgDialog(this.$t('Failed to save'), 'error');

      } finally {
        this.isSubmitting = false;
      }
    },

    submitMaintenanceType() {
      this.$refs.maintenanceTypeRules.validate().then(success => {
        if (success) {
          this.saveMaintenanceType()
        }
      })
    },

    async saveMaintenanceType() {
      this.isSubmittingType = true
      try {
        const response = await store.dispatch(`${this.MANAGEMENT_APP_STORE_MODULE_NAME}/addMaintenanceItemType`, {maintenanceItemType: this.maintenanceTypeForm});
        const newType = response.data.data;

        this.msgDialog(this.$t('Maintenance type created successfully'), 'success')
        this.showMaintenanceTypeModal = false

        if (this.editingItemIndex !== null && this.maintenanceForm.items[this.editingItemIndex]) {
          const item = this.maintenanceForm.items[this.editingItemIndex];
          item.maintenanceTypeId = newType.id;
          item.maintenanceTypeSearch = newType.name;
          item.filteredOptions = [];
        }

      } catch (error) {
        this.msgDialog(this.$t('Failed to create maintenance type'), 'error')
      } finally {
        this.isSubmittingType = false
        this.resetMaintenanceTypeForm();
      }
    },

    prepareNewMaintenanceTypeForm(name = '') {
      this.resetMaintenanceTypeForm();
      this.maintenanceTypeForm.name = name;
      this.maintenanceTypeForm.userId = this.user.id || null;
      this.maintenanceTypeForm.customerId = this.user.customerId || null;
    },

    resolveAssetLabel(asset) {
      if (!asset) return ''
      return asset.nickName || asset.nickname || asset.plate || asset.chassisNumber || ''
    },

    prefillAssetFromQuery() {
      const assetId = this.$route.query.assetId
      if (!assetId || this.myId) return

      this.isPrefillingAsset = true
      store
          .dispatch(`${this.MANAGEMENT_APP_STORE_MODULE_NAME}/fetchAsset`, { id: assetId })
          .then(response => {
            const asset = response.data && response.data.data ? response.data.data : response.data
            if (!asset) return

            this.maintenanceForm.assetId = asset.id || asset.assetId || assetId
            this.assetSearch = this.resolveAssetLabel(asset)
            this.isAssetPrefilled = Boolean(this.maintenanceForm.assetId)
          })
          .catch(() => {
            this.msgDialog(this.$t('Error searching assets'), 'error')
          })
          .finally(() => {
            this.$nextTick(() => {
              this.isPrefillingAsset = false
            })
          })
    },

    resetForm() {
      this.maintenanceForm = {
        assetId: '', preventiveMaintenanceId: null, note: '', value: 0,
        odometer: null, hourmeter: null, driverName: '', invoice: '',
        startDate: new Date(), endDate: null, additionalCode: '', items: []
      }
      this.assetSearch = ''
      this.preventiveMaintenanceSearch = ''
      this.isAssetPrefilled = false
      this.$refs.simpleRules.reset()
    },

    resetMaintenanceTypeForm() {
      this.maintenanceTypeForm = {
        customerId: null, userId: null, name: '', description: '',
        additionalCode: '', hoursExpectedLifetime: null, kmExpectedLifetime: null
      }
      this.editingItemIndex = null;
      if (this.$refs.maintenanceTypeRules) {
        this.$refs.maintenanceTypeRules.reset()
      }
    },

    fetchSearchMaintenanceItemType(query, callback) {
      store
          .dispatch(`${this.MANAGEMENT_APP_STORE_MODULE_NAME}/fetchSearchMaintenanceItemType`, {
            query,
            perPage: 10,
            page: 1
          })
          .then(response => {
            callback(response.data.data)
          })
          .catch(() => {
            this.msgDialog(this.$t('Error searching maintenance types'), 'error')
          })
    },

    fetchAssets(query, callback) {
      store
          .dispatch(`${this.MANAGEMENT_APP_STORE_MODULE_NAME}/fetchAssets`, {q: query})
          .then(response => {
            callback(response.data.data)
          })
          .catch(() => {
            this.msgDialog(this.$t('Error searching assets'), 'error')
          })
    },

    fetchPreventiveMaintenance(text, callback) {
      if (!this.maintenanceForm.assetId) return

      store
          .dispatch(`${this.MANAGEMENT_APP_STORE_MODULE_NAME}/fetchPreventiveMaintenances`, {search: text, assetId: this.maintenanceForm.assetId, page: 1, perPage: 5})
          .then(response => {
            callback(response.data.data)
          })
          .catch(() => {
            this.msgDialog(this.$t('Error searching maintenance plans'), 'error')
          })
    },

    msgDialog(msg, icon) {
      this.$swal({
        position: 'top-end', icon, title: msg,
        showConfirmButton: false, timer: 3000,
        customClass: {confirmButton: 'btn btn-primary'},
        buttonsStyling: false,
      })
    }
  },

  setup() {
    const MANAGEMENT_APP_STORE_MODULE_NAME = "app-management"
    const user = localStorage.userData != undefined ? JSON.parse(localStorage.userData) : {}
    if (!store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) {
      store.registerModule(MANAGEMENT_APP_STORE_MODULE_NAME, managementStoreModule)
    }
    onUnmounted(() => {
      if (store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(MANAGEMENT_APP_STORE_MODULE_NAME)
      }
    })
    return {
      MANAGEMENT_APP_STORE_MODULE_NAME,
      user
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';

.border-left-primary {
  border-left: 3px solid #007bff !important;
}

.vue-autosuggest__input {
  width: 100%;
}

.vue-autosuggest__results-container {
  position: relative;
  width: 100%;
}

.vue-autosuggest__results {
  position: absolute;
  z-index: 1050;
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
  background: white;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
  padding: 0;
  list-style: none;
}

.vue-autosuggest__results ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.vue-autosuggest__results li {
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #efefef;
}

.vue-autosuggest__results li:last-child {
  border-bottom: none;
}

.vue-autosuggest__results li:hover,
.vue-autosuggest__results .vue-autosuggest__results-item--highlighted {
  background-color: #f0f0f0;
}
</style>